/** Fixed-step combat rules. World coordinates use 1/64 units; body and pilot states are independent. */
import { SeededRandom } from './rng.js';
import { scaledSin, scaledCos, wrapDeg, moveDelta } from './math.js';
import { FRAME_HEADINGS } from './tables.js';
export type Side = 0 | 1;
export interface PlaneInput {
  climb: boolean;
  dive: boolean;
  left: boolean;
  right: boolean;
  fire: boolean;
  eject?: boolean;
}
export const EMPTY_INPUT: PlaneInput = {
  climb: false,
  dive: false,
  left: false,
  right: false,
  fire: false,
  eject: false,
};
export interface Plane {
  x: number;
  y: number;
  d: number;
  e: number;
  h: number;
  g: number;
  a: number;
  f: number;
  i: number;
  j: number;
  k: number;
  v: number;
  l: number;
  r: number;
  s: number;
  hitP: number;
  as: boolean;
  side: Side;
  pilotX: number;
  pilotY: number;
  vx: number;
  vy: number;
  aa: number;
  ab: number;
  ac: number;
  ad: number;
  t: number;
  evade: boolean;
  perfect: boolean;
  active: boolean;
  completedShots: number;
  hits: number;
  accuracy: number;
  portrait: number;
  resetSerial: number;
}
export interface Bullet {
  x: number;
  y: number;
  h: number;
  age: number;
  owner: Side;
}
export type SimEvent =
  | {
      type: 'crash';
      side: Side;
      x: number;
      y: number;
      h: number;
    }
  | {
      type: 'music';
      slot: number;
    }
  | {
      type: 'fire' | 'impact' | 'reset' | 'score';
      side: Side;
    };
export interface SpriteDraw {
  name: string;
  x: number;
  y: number;
  anchor: 3 | 33;
}
export interface PlaneFrame {
  plane: Plane;
  explosion: SpriteDraw | null;
  bullets: {
    x: number;
    y: number;
  }[];
  body: SpriteDraw | null;
  pilot: SpriteDraw[];
}
export interface SimState {
  tick: number;
  planes: [Plane, Plane];
  bullets: Bullet[];
  seed: number;
  cam: {
    x: number;
    y: number;
  };
  score: [number, number];
  roundPause: number;
  over: boolean;
  winner: Side | null;
  practice: boolean;
  taunt: {
    side: Side;
    index: number;
    ticks: number;
  } | null;
  events: SimEvent[];
  frames: [PlaneFrame, PlaneFrame];
}
export const SIM_STEP_MS = 50;
export const SIM_HZ = 1000 / SIM_STEP_MS;
export const GUN_COOLDOWN = 10;
export const TURN_COOLDOWN = 2;
export const BULLET_LIFE = 20;
export const BULLET_FREE_Y = 255;
export const GAUGE_CLIMB = 16;
export const GAUGE_DESCEND = 8;
export const GAUGE_MAX = 250;
export const GAUGE_HARD_MAX = 350;
export const GAUGE_LIFT_MIN = 230;
export const LOOP_JUMP_MID = 128;
export const LOOP_JUMP_TOP = 64;
export const WORLD_MIN_X = 109;
export const WORLD_MAX_X = 145;
export const BULLET_ZONE_X0 = 109;
export const BULLET_ZONE_X1 = 145;
export const BULLET_ZONE_Y0 = 168;
export const BULLET_ZONE_Y1 = 189;
export const HIT_DIST2 = 30;
export const HIT_DAMAGE_DIST2 = 80;
export const HILL_X0 = 104;
export const HILL_X1 = 150;
export const HILL_MIN_E = 164;
export const SEA_LEVEL_E = 182;
export const ARENA_W = 256;
export const ARENA_H = 209;
export const CEILING_GAUGE_PENALTY = 10;
export const DUEL_TARGET_X = [239, 16];
export const IDLE_WRAP_TICKS = 50;
export const KILL_TARGET = 10;
export const CAM_MAX_X = 127;
export const CAM_MAX_Y = 80;
export const GROUND_EXPLOSION_FRAMES = 5;
export const PILOT_STATS: ReadonlyArray<{
  ag: number;
  ah: number;
  ai: number;
  aj: number;
  ak: number;
  al: number;
  am: number;
}> = [
  { ag: 8, ah: 65, ai: 0, aj: 5, ak: 20, al: 15, am: 2 },
  { ag: 4, ah: 45, ai: 60, aj: 8, ak: 15, al: 10, am: 1 },
  { ag: 2, ah: 30, ai: 30, aj: 10, ak: 10, al: 0, am: 0 },
  { ag: 1, ah: 30, ai: 30, aj: 10, ak: 10, al: 0, am: 0 },
];
export const TAUNT_TICKS = 45;
export function frameForHeading(h: number): number {
  return (Math.round(wrapDeg(h) / 22.5) + 12) % 16;
}
export function pilotPosition(p: Plane): {
  x: number;
  y: number;
} {
  return p.v === 0 ? { x: p.d, y: p.e } : { x: p.pilotX >> 6, y: p.pilotY >> 6 };
}
const sprite = (group: string, index: number, x: number, y: number): SpriteDraw => ({
  name: `${group}_${String(index).padStart(2, '0')}`,
  x,
  y,
  anchor: 3,
});
function frame(p: Plane): PlaneFrame {
  return {
    plane: { ...p },
    explosion: null,
    bullets: [],
    body: p.a < 2 ? sprite('plane', p.f + (p.i === 3 && p.side === 0 ? 16 : 0), p.d, p.e) : null,
    pilot: [],
  };
}
export function createSim(
  seed: number,
  options: {
    practice?: boolean;
    playerPilot?: number;
    opponentPilot?: number;
  } = {},
): SimState {
  const mk = (side: Side): Plane => ({
    x: (side === 0 ? 16 : 239) * 64,
    y: 180 * 64,
    d: side === 0 ? 16 : 239,
    e: 180,
    h: side === 0 ? 90 : 270,
    g: 0,
    a: 0,
    f: 1,
    i: side === 0 ? 3 : 2,
    j: 0,
    k: 0,
    v: 0,
    l: 0,
    r: 255,
    s: 0,
    hitP: 0,
    as: false,
    side,
    pilotX: 0,
    pilotY: 0,
    vx: 0,
    vy: 0,
    aa: 0,
    ab: 0,
    ac: 0,
    ad: 0,
    t: 0,
    evade: false,
    perfect: true,
    active: true,
    completedShots: 0,
    hits: 0,
    accuracy: 0,
    portrait: side === 0 ? 35 + (options.playerPilot ?? 0) : 39 + (options.opponentPilot ?? 0),
    resetSerial: 0,
  });
  const planes: [Plane, Plane] = [mk(0), mk(1)];
  return {
    tick: 0,
    planes,
    bullets: [0, 0, 1, 1].map((owner) => ({ x: 0, y: 255, h: 0, age: 0, owner: owner as Side })),
    seed: Number(new SeededRandom(seed).rawSeed()),
    cam: { x: 0, y: 80 },
    score: [0, 0],
    roundPause: 0,
    over: false,
    winner: null,
    practice: options.practice ?? false,
    taunt: null,
    events: [],
    frames: [frame(planes[0]), frame(planes[1])],
  };
}
export function resetPlane(state: SimState, side: Side): void {
  const p = state.planes[side];
  p.x = (side === 0 ? 16 : 239) * 64;
  p.y = 180 * 64;
  p.a = 0;
  p.g = 0;
  p.f = 1;
  p.h = p.i === 2 ? 270 : 90;
  p.r = 255;
  p.s = 0;
  p.hitP = 0;
  p.evade = false;
  p.as = false;
  p.v = 0;
  p.active = true;
  p.resetSerial++;
  for (const b of state.bullets) if (b.owner === side) b.y = 255;
  state.events.push({ type: 'reset', side });
}
export function climb(p: Plane): void {
  if (p.v !== 0) return;
  p.as = true;
  if (p.a === 0) {
    p.g += 16;
    if (p.f === 1 && p.g > 125) {
      p.y -= 128;
      p.f = 0;
    }
    if (p.g > 250) {
      p.y -= 64;
      p.g = 250;
      p.a = 1;
      p.as = false;
    }
  } else if (p.a === 1 && p.g <= 250)
    p.g = Math.min(250, p.g + ((Math.abs(scaledSin(p.h)) * 8) >> 7));
}
export function dive(p: Plane): void {
  if (p.v !== 0) return;
  p.as = false;
  if (p.a === 1) p.g = Math.max(0, p.g - 8);
}
function heading(p: Plane): void {
  p.h = FRAME_HEADINGS[p.f + (p.i === 2 ? 0 : 16)]!;
}
export function turnPlane(state: SimState, side: Side, dir: 2 | 3): void {
  const p = state.planes[side];
  if (p.j > 0 || p.v !== 0) {
    if (p.v === 2) {
      p.vx = dir === 2 ? Math.max(-45, p.vx - 10) : Math.min(45, p.vx + 10);
      p.ac = dir === 2 ? 0 : 2;
    } else if (p.v === 4) {
      p.pilotX += dir === 2 ? -100 : 100;
      p.aa = dir === 2 ? (p.aa === 2 ? 3 : 2) : p.aa === 4 ? 5 : 4;
      const x = p.pilotX >> 6;
      if (x > 109 && x < 145) resetPlane(state, side);
    }
    return;
  }
  if (p.a === 0) return;
  p.f = (p.f + ((p.i === 2 && dir === 2) || (p.i === 3 && dir === 3) ? 15 : 1)) % 16;
  heading(p);
  p.j = 2;
}
export function eject(state: SimState, side: Side): void {
  const p = state.planes[side];
  if (p.a === 2) return;
  if (p.a === 1 && p.v === 0) {
    p.pilotX = p.x;
    p.pilotY = p.y;
    p.v = 1;
    const angle = wrapDeg(p.h + (p.i === 2 ? 90 : -90));
    p.vx = (scaledSin(angle) * 300) >> 7;
    p.vy = (-scaledCos(angle) * 300) >> 7;
    state.events.push({ type: 'music', slot: 5 });
  } else if (p.v === 1) {
    p.vy = Math.max(20, p.vy - 60);
    p.v = 2;
    p.ac = 1;
  }
}
export function fire(state: SimState, side: Side): void {
  const p = state.planes[side];
  if (p.k > 0 || p.v !== 0 || p.a !== 1) return;
  for (let i = 1; i >= 0; i--) {
    const b = state.bullets[side * 2 + i]!;
    if (b.y !== 255) continue;
    b.x = p.x >> 6;
    b.y = p.y >> 6;
    b.h = p.h;
    b.age = 0;
    p.k = 10;
    state.events.push({ type: 'fire', side });
    return;
  }
}
export function applyInput(state: SimState, side: Side, input: PlaneInput): void {
  const p = state.planes[side];
  if (input.climb) climb(p);
  if (input.dive) dive(p);
  if (input.left) turnPlane(state, side, 2);
  if (input.right) turnPlane(state, side, 3);
  if (input.fire) fire(state, side);
  if (input.eject) eject(state, side);
}
export interface Controller {
  poll(state: SimState, side: Side, rng: SeededRandom): PlaneInput;
  act?(state: SimState, side: Side, rng: SeededRandom): void;
}
export function crashPlane(state: SimState, side: Side): void {
  const p = state.planes[side];
  p.a = 2;
  p.f = 0;
  p.hitP = 0;
  p.perfect = false;
  state.events.push({ type: 'crash', side, x: p.d, y: p.e, h: p.h }, { type: 'music', slot: 3 });
}
function taunt(state: SimState, side: Side, rng: SeededRandom): void {
  const p = state.planes[side];
  state.taunt = {
    side,
    ticks: TAUNT_TICKS,
    index:
      (p.portrait - 35) * 4 + (state.score[side] < state.score[1 - side]! ? 2 : 0) + rng.below(2),
  };
}
function freeBullet(state: SimState, b: Bullet, hit: boolean): void {
  b.y = 255;
  const p = state.planes[b.owner];
  p.completedShots++;
  if (hit) p.hits++;
  p.accuracy = Math.trunc((p.hits * 100) / p.completedShots);
}
function updateBullets(state: SimState, side: Side, out: PlaneFrame, rng: SeededRandom): void {
  const p = state.planes[side],
    foe = state.planes[(1 - side) as Side];
  const fx = foe.x >> 6,
    fy = foe.y >> 6,
    px = foe.pilotX >> 6,
    py = foe.pilotY >> 6;
  for (let i = 1; i >= 0; i--) {
    const b = state.bullets[side * 2 + i]!;
    if (b.y === 255) continue;
    if (++b.age > 20) {
      freeBullet(state, b, false);
      continue;
    }
    b.x += moveDelta(scaledSin(b.h));
    b.y -= moveDelta(scaledCos(b.h));
    if (b.y > 189 || (b.y > 168 && b.x > 109 && b.x < 145)) {
      p.r = b.x + 1;
      freeBullet(state, b, false);
      state.events.push({ type: 'impact', side });
    }
    out.bullets.push({ x: b.x, y: b.y });
    if (b.y === 255 || !foe.active) continue;
    let dx = Math.abs(b.x - fx),
      dy = Math.abs(b.y - fy);
    if (foe.a < 2 && dx * dx + dy * dy < 80) {
      foe.hitP++;
      foe.perfect = false;
      if (foe.hitP > 2) {
        crashPlane(state, foe.side);
        if (foe.v === 0) {
          state.score[side]++;
          state.events.push({ type: 'score', side });
        }
      } else state.events.push({ type: 'music', slot: 2 });
      freeBullet(state, b, true);
    }
    if (foe.v > 0 && foe.v !== 3) {
      dx = Math.abs(b.x - px);
      dy = Math.abs(b.y - py);
      if (dx * dx + dy * dy < 30) {
        foe.v = 3;
        foe.aa = 0;
        freeBullet(state, b, true);
        taunt(state, side, rng);
        state.score[side] += 2;
        state.events.push({ type: 'score', side });
      }
    }
    if (foe.v === 2 && dx < 10 && Math.abs(b.y - (py - 10)) < 5) foe.v = 5;
  }
}
function updatePlane(state: SimState, side: Side, rng: SeededRandom): void {
  const p = state.planes[side];
  if (p.as) climb(p);
  if (p.a === 0) p.x += p.i === 2 ? -p.g : p.g;
  else if (p.a === 1) {
    p.x += (scaledSin(p.h) * p.g) >> 7;
    const moveY = (scaledCos(p.h) * p.g) >> 7;
    p.y -= moveY;
    if (p.f > 1 && p.f < 7) p.g = Math.max(0, p.g - (scaledCos(p.h) >> 5));
    else if (p.f > 9 && p.f < 15) p.g = Math.min(350, p.g - (scaledCos(p.h) >> 5));
    const lift = p.g < 230 ? Math.trunc((250 * (230 - p.g)) / 230) : 0;
    p.y += lift;
    if (lift > moveY) p.as = false;
  } else if (p.a === 3 && ++p.l > 50) {
    p.l = 0;
    if (p.v === 0) resetPlane(state, side);
  }
  if (p.v === 1 || p.v === 5) {
    p.pilotX += p.vx;
    p.pilotY += p.vy;
    p.vy += 10;
    if (p.vx > 8) p.vx -= 8;
    else if (p.vx < -8) p.vx += 8;
    p.aa = p.aa === 0 ? 1 : 0;
  } else if (p.v === 2) {
    p.pilotX += p.vx;
    p.pilotY += p.vy;
    if (++p.ad > 1) {
      p.ad = 0;
      p.vy = Math.max(70, p.vy - 15);
    }
    if (p.vx > 8) p.vx -= 8;
    else if (p.vx < -8) p.vx += 8;
    else p.ac = 1;
    if (++p.ab > 3) {
      p.ab = 0;
      p.aa = p.aa === 0 ? 1 : 0;
    }
  } else if (p.v === 3) {
    p.pilotY -= 30;
    if (p.aa !== 3) {
      if (++p.ab > 1) {
        p.ab = 0;
        if (++p.aa === 3) p.aa = 0;
      }
      if (++p.ad === 40) {
        p.ad = 0;
        p.aa = 3;
      }
    } else if (++p.ad > 3) {
      p.ad = 0;
      resetPlane(state, side);
    }
  }
  if (p.j > 0) p.j--;
  if (p.k > 0) p.k--;
  p.d = p.x >> 6;
  p.e = p.y >> 6;
  const px = p.pilotX >> 6,
    py = p.pilotY >> 6;
  if (p.a < 2 && (p.e > 182 || (p.e > 164 && p.d > 104 && p.d < 150))) {
    crashPlane(state, side);
    if (p.v === 0) {
      state.score[side] = Math.max(0, state.score[side] - 1);
      state.events.push({ type: 'score', side });
    }
  }
  if ((p.v === 1 || p.v === 2 || p.v === 5) && py > 185) {
    p.pilotY = 185 * 64;
    if (p.vy > 160) {
      p.v = 3;
      state.score[side] = Math.max(0, state.score[side] - 1);
      state.events.push({ type: 'score', side });
      taunt(state, (1 - side) as Side, rng);
    } else {
      p.v = 4;
      if (px > 109 && px < 145) resetPlane(state, side);
    }
  }
  if (p.v !== 0 && p.j === 0) {
    if (p.f < 5 || p.f > 12) p.f = (p.f + 15) % 16;
    else if (p.f > 4 && p.f < 12) p.f++;
    heading(p);
    p.j = 5;
  }
  if (p.d < 0) {
    p.d = 255;
    p.x = 255 * 64;
  } else if (p.d > 255) {
    p.d = 0;
    p.x = 0;
  }
  if (px < 0) p.pilotX = 255 * 64;
  else if (px > 255) p.pilotX = 0;
  if (p.e < 0) {
    p.e = 0;
    p.y = 0;
    p.g -= 10;
  } else if (p.e > 208) {
    p.e = 0;
    p.y = 0;
  }
  const out: PlaneFrame = { plane: { ...p }, explosion: null, bullets: [], body: null, pilot: [] };
  if (p.r !== 255) {
    out.explosion = { ...sprite('fx', 30 + p.s, p.r, 189), anchor: 33 };
    if (++p.s === 5) {
      p.r = 255;
      p.s = 0;
    }
  }
  updateBullets(state, side, out, rng);
  if (p.a < 2) out.body = sprite('plane', p.f + (p.i === 3 && p.side === 0 ? 16 : 0), p.d, p.e);
  else if (p.a === 2) {
    if (p.f < 7) {
      out.body = sprite('fx', 2 + p.f, p.d, p.e);
      p.f++;
    } else {
      p.a = 3;
      if (p.v === 0) taunt(state, (1 - side) as Side, rng);
    }
  }
  if (p.v === 2) out.pilot.push(sprite('fx', 27 + p.ac, p.pilotX >> 6, (p.pilotY >> 6) - 10));
  if (p.v === 1 || p.v === 2 || p.v === 4 || p.v === 5)
    out.pilot.push(sprite('fx', 21 + p.aa, p.pilotX >> 6, p.pilotY >> 6));
  else if (p.v === 3) out.pilot.push(sprite('fx', 17 + p.aa, p.pilotX >> 6, p.pilotY >> 6));
  out.plane = { ...p };
  state.frames[side] = out;
}
function tick(
  state: SimState,
  control: (side: Side, rng: SeededRandom) => void,
  rng: SeededRandom,
): SimState {
  if (state.over) return state;
  state.tick++;
  state.events = [];
  control(0, rng);
  const p = pilotPosition(state.planes[0]);
  state.cam.x = Math.max(0, Math.min(127, p.x - 64));
  state.cam.y = Math.max(0, Math.min(80, p.y - 64));
  control(1, rng);
  updatePlane(state, 1, rng);
  updatePlane(state, 0, rng);
  if (
    state.taunt &&
    --state.taunt.ticks === 0 &&
    !state.practice &&
    (state.score[0] >= 10 || state.score[1] >= 10)
  ) {
    state.over = true;
    state.winner = state.score[0] >= 10 ? 0 : 1;
  }
  state.seed = Number(rng.rawSeed());
  return state;
}
export function step(
  state: SimState,
  inputs: readonly [PlaneInput, PlaneInput],
  externalRng?: SeededRandom,
): SimState {
  return tick(
    state,
    (side) => applyInput(state, side, inputs[side]),
    externalRng ?? SeededRandom.fromRaw(BigInt(state.seed)),
  );
}
export function stepWithControllers(
  state: SimState,
  controllers: readonly [Controller, Controller],
): SimState {
  return tick(
    state,
    (side, rng) => {
      const c = controllers[side];
      if (c.act) c.act(state, side, rng);
      else applyInput(state, side, c.poll(state, side, rng));
    },
    SeededRandom.fromRaw(BigInt(state.seed)),
  );
}
export function canonicalState(state: SimState): string {
  const { frames: _frames, events: _events, ...rules } = state;
  return JSON.stringify(rules);
}
export function hashState(state: SimState): string {
  let hash = 0x811c9dc5;
  for (const c of canonicalState(state)) hash = Math.imul(hash ^ c.charCodeAt(0), 0x01000193);
  return (hash >>> 0).toString(16).padStart(8, '0');
}
